import { FC } from 'react'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { Close } from 'svgIcons'
import { Notification_SimpleNotificationFragment } from 'generated/graphql'
import { convertCreatedAtToString } from '@app.modules/util/time'
import MentorApproveIcon from 'svgIcons/MentorApproveIcon'
import ActivityType from 'svgIcons/notification/ActivityType'
import ChannelType from 'svgIcons/notification/ChannelType'
import ChatReplyMessageType from 'svgIcons/notification/ChatReplyMessageType'
import CommentMentorPostIcon from 'svgIcons/notification/CommentMentorPostIcon'
import CommunityType from 'svgIcons/notification/CommunityType'
import MentorRewardIcon from 'svgIcons/notification/MentorRewardIcon'
import RecommendMentorPostIcon from 'svgIcons/notification/RecommendMentorPostIcon'
import {
  handleClickDeleteNotificationType,
  handleClickNotificationType,
  NotificationType,
  NotificationTypes,
} from './NotificationCard.types'

gql`
  fragment Notification_SimpleNotification on SimpleNotification {
    id
    __typename
    title
    content
    unread
    linkUrl
    deepLinkUrl
    createdAt
  }
`

const isNotificationType = (
  type: NotificationType | unknown,
): type is NotificationType => {
  if (NotificationTypes.includes(type as NotificationType)) {
    return true
  }

  return false
}

export const getTypeData = (type: NotificationType | unknown) => {
  if (isNotificationType(type)) {
    switch (type) {
      case 'ActivityApprovedNotification':
        return {
          ico: <ActivityType />,
          category: '공고',
        }
      case 'ActivityRefusedNotification':
        return {
          ico: <ActivityType />,
          category: '공고',
        }
      case 'BookmarkToNewsItemNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'CommentToCommentNotification':
        return {
          ico: <CommunityType />,
          category: '커뮤니티',
        }
      case 'CommentToPostNotification':
        return {
          ico: <CommunityType />,
          category: '커뮤니티',
        }
      case 'FollowToChannelNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'LikeToNewsItemNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'NewActivityNotification':
        return {
          ico: <ActivityType />,
          category: '공고',
        }
      case 'NewNewsItemNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'NewsCommentToNewsCommentNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'NewsCommentToNewsItemNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'OrganizationApprovedNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'OrganizationPendingNotification':
        return {
          ico: <ChannelType />,
          category: '채널',
        }
      case 'ReplyToActivityNotification':
        return {
          ico: <ActivityType />,
          category: '공고',
        }
      case 'ReplyToReplyNotification':
        return {
          ico: <CommunityType />,
          category: '커뮤니티',
        }
      case 'ScrapActivityCloseSoonNotification':
        return {
          ico: 'D-3',
          category: '공고',
        }
      case 'ScrapActivityCloseSoonerNotification':
        return {
          ico: 'D-1',
          category: '공고',
        }
      case 'ChatMessageReplyNotification':
        return {
          ico: <ChatReplyMessageType />,
          category: '채팅',
        }
      case 'AcceptedMentorCommentRewardNotification':
        return {
          ico: <MentorRewardIcon />,
          category: '커뮤니티',
        }
      case 'MatchedMentorCommentRewardNotification':
        return {
          ico: <MentorRewardIcon />,
          category: '커뮤니티',
        }
      case 'CommentToMentorPostNotification':
        return {
          ico: <CommentMentorPostIcon />,
          category: '커뮤니티',
        }
      case 'RecommendedMentorPostNotification':
        return {
          ico: <RecommendMentorPostIcon />,
          category: '커뮤니티',
        }
      case 'MentorApprovedNotification':
        return {
          ico: <MentorApproveIcon />,
          category: '커뮤니티',
        }
    }
  }

  return {
    ico: '',
    category: '',
  }
}

interface IFNotificationCardProps {
  data: Notification_SimpleNotificationFragment
  onClick: handleClickNotificationType
  onClickDelete: handleClickDeleteNotificationType
}

const NotificationCard: FC<IFNotificationCardProps> = ({
  data,
  onClick,
  onClickDelete,
}) => {
  const router = useRouter()
  const typeData = getTypeData(data.__typename)

  const handleClickNotification = (
    data: Notification_SimpleNotificationFragment,
  ) => {
    onClick(data.id)

    if (data?.__typename === 'ChatMessageReplyNotification') {
      router.replace(
        `${router.asPath}${router.query.tab ? '&' : '?'}${
          data.linkUrl.split('?')[1]
        }`,
      )
    } else {
      router.push(data.linkUrl)
    }
  }

  return (
    <StyledWrapper data-new={data.unread}>
      <div className="lt-section" onClick={() => handleClickNotification(data)}>
        <div
          className={
            typeof typeData.ico === 'string' ? 'type-text' : 'type-ico'
          }
        >
          {typeData.ico}
        </div>
        <div className="notification-info">
          <div className="notiinfo-title">{data.title}</div>
          <div className="notiinfo-desc">{data.content}</div>
          <div className="notiinfo-footer">
            <span>{typeData.category}</span>
            <span className="notiinfo-hr">|</span>
            <span>{convertCreatedAtToString(data.createdAt)}</span>
          </div>
        </div>
      </div>
      <div className="rt-section" onClick={() => onClickDelete(data.id)}>
        <div className="close-btn">
          <Close />
        </div>
      </div>
    </StyledWrapper>
  )
}

export default NotificationCard

const StyledWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
  border: 1px solid #eeeeee;
  border-radius: 5px;
  &[data-new='true'] {
    background-color: #f2faff;
  }
  .lt-section {
    cursor: pointer;
    padding: 0px 28px;
    display: flex;
    gap: 16px;
    .type-ico,
    .type-text {
      width: 41px;
      height: 41px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      color: white;
      background-color: #01a0ff;
    }
    .type-text {
      background-color: #01a0ff;
    }

    .notification-info {
      .notiinfo-title {
        .count {
          color: #01a0ff;
        }
      }
      .notiinfo-desc {
        font-size: 12px;
        color: #999;
        margin-bottom: 7px;
      }
      .notiinfo-footer {
        font-size: 12px;
        color: #999;
        .notiinfo-hr {
          padding: 0px 10px;
        }
      }
    }
  }
  .rt-section {
    padding: 0px 16px;
    .close-btn {
      display: flex;
      cursor: pointer;
      svg {
        width: 10px;
        height: 10px;
      }
    }
  }
`
